import { useRecoilValue } from "recoil";

import {
  apiDataFetchingAtom,
  ScrollAreaEnum,
  useScrollPositionMemory,
} from "@ryde/shared";

import { usePageContext } from "../contexts";

/**
 * Binds a scrollable container to the current page's scroll memory: a component hands over its
 * element and nothing else. What identifies the page, and what counts as a refresh, are resolved
 * here rather than in each container, so no display can wire them differently or forget one.
 *
 * The element stays the caller's to supply because it is acquired differently everywhere — a ref
 * callback, a setter passed down to a child, a react-window imperative API — and that part is
 * presentation plumbing.
 *
 * `intentElement` is where a scroll the user asked for is watched for, when that is not the
 * scrolling element itself: `VirtualizedGrid` handles wheel events on the ancestor that owns all
 * four panes, so a wheel over the frozen header never reaches the main pane.
 */
export const usePageScrollPositionMemory = ({
  element,
  area,
  intentElement,
  onApplied,
}: {
  element: HTMLElement | null;
  area: ScrollAreaEnum;
  intentElement?: HTMLElement | null;
  onApplied?: () => void;
}): void => {
  const { entity } = usePageContext();
  const isRefreshing = useRecoilValue(apiDataFetchingAtom);

  useScrollPositionMemory({
    element,
    area,
    pageKey: entity,
    isRefreshing,
    intentElement,
    onApplied,
  });
};
